import { Injectable } from '@angular/core';
import { SelectableList } from './selectable-list';

/**
 * Which of the user-access columns are on screen right now, keyed by the id field they select
 * on (`connectUserId`, `tenantId`, `roleInvariantKey`, …).
 *
 * The three columns each render one list at a time through an `@switch` on the chosen entity
 * type, so one id key means one list. The keys do not overlap between columns: the subject
 * column selects on `connectUserId` or `userGroupInvariantKey`, the context column on
 * `tenantId` or `companyGroupInvariantKey`, the assignment column on `roleInvariantKey`.
 *
 * Nothing here selects anything — it only answers which lists exist, so a caller can tell
 * "the user is not on that screen" from "that id was not found".
 */
@Injectable({ providedIn: 'root' })
export class SelectableListRegistry {

    private readonly lists = new Map<string, SelectableList>();

    register(idKey: string, list: SelectableList): void {
        this.lists.set(idKey, list);
    }

    /**
     * Guarded by identity, which is not defensive padding: switching a column's type destroys
     * one list and creates another, and Angular creates the replacement BEFORE destroying the
     * one it replaces. An unconditional delete on destroy would therefore throw away the
     * registration the new list had just made, and the column would look absent.
     */
    unregister(idKey: string, list: SelectableList): void {
        if (this.lists.get(idKey) === list) {
            this.lists.delete(idKey);
        }
    }

    get(idKey: string): SelectableList | undefined {
        return this.lists.get(idKey);
    }

    /** The id keys currently on screen. */
    mountedIdKeys(): string[] {
        return [...this.lists.keys()];
    }
}
